import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import { trackContentSave } from '../../lib/gaEvents';
import { Typography, Box, Divider, Stack, useMediaQuery } from '@mui/material';
import CommentSection from '../../components/comment/CommentSection';
import { playlistApi } from '../../api/playlist';
import { commentApi } from '../../api/comment';
import PlaylistAlbumCard from '../../components/card/PlaylistAlbumCard';
import PlaylistTrackCard from '../../components/card/PlaylistTrackCard';
import ProgressBar from '../../components/common/ProgressBar';
import { useUserStore } from '../../stores/userStore';
import PageLayout from '../../components/layout/PageLayout';
import EditDeleteMenuButton from '../../components/button/EditDeleteMenuButton';
import AdminDeleteButton from '../../components/button/AdminDeleteButton';
import UserWithProfile from '../../components/common/UserWithProfile';
import CommentCount from '../../components/common/CommentCount';
import BookmarkCount from '../../components/common/BookmarkCount';
import NoData from '../../components/common/NoData';
import BookmarkButton from '../../components/button/BookmarkButton';
import theme from '../../theme';
import AlbumCountWithDate from '../../components/common/AlbumCountWithDate';
import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import { Helmet } from 'react-helmet-async';
import SidebarLayout from '../../components/layout/SidebarLayout';
import ShareButton from '../../components/button/ShareButton';
import defaultPlaylistThumbnail from '../../assets/default_playlist_thumbnail.svg';
import InFeedBoxAd from '../../components/ad/InFeedBoxAd';
import { SITE_URL } from '../../utils/env';
import { embedYouTubeInText } from '../../utils/youtube';

const PlaylistDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const user = useUserStore((state) => state.user);
  const isAdmin = useUserStore((state) => state.isAdmin);
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const listContentType =
    playlist?.type === 'TRACK' ? CONTENT_TYPE.LIST_SONG : CONTENT_TYPE.LIST_ALBUM;
  useTrackPageView(
    {
      pageType: PAGE_TYPE.LIST_POST,
      contentType: listContentType,
      contentId: playlist?.id,
      contentIdPrefix: 'list',
      contentName: playlist?.title,
    },
    { ready: !!playlist?.id },
  );
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [comments, setComments] = useState([]);

  // 댓글 스크롤: path parameter에서 commentId 감지
  const commentViewMatch = location.pathname.match(/\/comment\/(\d+)$/);
  const commentScrollId = commentViewMatch ? parseInt(commentViewMatch[1]) : null;

  useEffect(() => {
    const fetchPlaylist = async () => {
      try {
        // 숫자 ID이면 기존 방식, 슬러그이면 by-slug 엔드포인트 사용
        const isNumericId = /^\d+$/.test(slug);
        const playlistRes = isNumericId
          ? await playlistApi.getPlaylistById(slug)
          : await playlistApi.getPlaylistBySlug(slug);
        const playlistData = playlistRes.data;
        const commentsRes = await commentApi.getComments('play_list', playlistData.id);
        setComments(commentsRes.data);
        setPlaylist(playlistData);
        setIsBookmarked(playlistData.isBookmarked);
      } catch (err) {
        console.error('Failed to fetch playlist:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlaylist();
  }, [slug]);

  const handleBookmarkToggle = async () => {
    try {
      if (isBookmarked) {
        await playlistApi.deleteBookmark(playlist.id);
        setPlaylist((prev) => ({
          ...prev,
          bookmarkCount: prev.bookmarkCount - 1,
        }));
      } else {
        await playlistApi.addBookmark(playlist.id);
        setPlaylist((prev) => ({
          ...prev,
          bookmarkCount: prev.bookmarkCount + 1,
        }));
        trackContentSave(
          {
            contentType: CONTENT_TYPE.LIST,
            contentId: playlist.id,
            contentName: playlist.title,
            pageType: PAGE_TYPE.LIST_POST,
          },
          user,
        );
      }
      setIsBookmarked(!isBookmarked);
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
    }
  };

  const handleEditClick = () => {
    navigate(`/lists/${playlist?.id}/edit?from=detail`);
  };

  const handleDeleteClick = async () => {
    try {
      await playlistApi.deletePlaylist(playlist.id);
      navigate('/lists');
    } catch (err) {
      console.error('Failed to delete playlist:', err);
    }
  };

  const handleCommentSubmit = async (content) => {
    const { data } = await playlistApi.addComment(playlist.id, content);
    setPlaylist((prev) => ({ ...prev, commentCount: prev.commentCount + 1 }));
    return data;
  };

  const handleCommentReply = async (parentId, content, mentionedUserId) => {
    const { data } = await playlistApi.addComment(playlist.id, content, parentId, mentionedUserId);
    setPlaylist((prev) => ({ ...prev, commentCount: prev.commentCount + 1 }));
    return data;
  };

  const handleCommentDelete = () => {
    setPlaylist((prev) => ({ ...prev, commentCount: prev.commentCount - 1 }));
  };

  const isOwner = user && user.id === playlist?.userId;
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const playlistShareTitle =
    playlist?.title && playlist?.username
      ? `${playlist.title} — ${playlist.username}`
      : playlist?.title;
  const renderPlaylistActions = () => (
    <Box display="flex" gap={1} sx={{ flexShrink: 0 }}>
      <ShareButton
        url={`/lists/${playlist.id}`}
        contentType={CONTENT_TYPE.LIST}
        contentId={playlist.id}
        contentName={playlist.title}
        shareTitle={playlistShareTitle}
        shareText={playlistShareTitle}
        pageType={PAGE_TYPE.LIST_POST}
      />
      <BookmarkButton isBookmarked={isBookmarked} onToggle={handleBookmarkToggle} />
      {isOwner ? (
        <EditDeleteMenuButton onEdit={handleEditClick} onDelete={handleDeleteClick} />
      ) : isAdmin ? (
        <AdminDeleteButton onDelete={handleDeleteClick} />
      ) : null}
    </Box>
  );

  return (
    <PageLayout maxWidth={'980px'}>
      {playlist && (
        <>
          <OpenInAppBanner
            type="playlist"
            title={playlist.title}
            image={playlist.imageUrl || playlist.previewImageUrls?.[0]}
          />
          <SEO
            title={playlist.title}
            description={playlist.description}
            image={playlist.imageUrl}
            url={
              playlist.slug
                ? `${SITE_URL}/lists/${playlist.slug}`
                : `${SITE_URL}/lists/${playlist.id}`
            }
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                  { '@type': 'ListItem', position: 2, name: '플리', item: `${SITE_URL}/lists` },
                  { '@type': 'ListItem', position: 3, name: playlist.title },
                ],
              })}
            </script>
          </Helmet>
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: playlist.title,
                image: playlist.imageUrl ? [playlist.imageUrl] : undefined,
                datePublished: playlist.createdAt?.replace(
                  /(\d+)\.(\d+)\.(\d+)/,
                  (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                ),
                dateModified: playlist.updatedAt?.replace(
                  /(\d+)\.(\d+)\.(\d+)/,
                  (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                ),
                author: {
                  '@type': 'Person',
                  name: playlist.username,
                  url: `${SITE_URL}/users/${encodeURIComponent(playlist.username)}`,
                },
              })}
            </script>
          </Helmet>
          {(() => {
            const items =
              playlist.type === 'TRACK'
                ? playlist.tracks
                    ?.filter((t) => t.albumImageUrl)
                    .map((t, i) => ({
                      '@type': 'ListItem',
                      name: t.title,
                      image: t.albumImageUrl,
                      url: t.albumSlug
                        ? `${SITE_URL}/reviews/${t.albumSlug}`
                        : t.albumSourceId
                          ? `${SITE_URL}/reviews/${t.albumSource?.toLowerCase()}/${t.albumSourceId}`
                          : `${SITE_URL}/reviews/local/${t.albumId}`,
                      position: i + 1,
                    }))
                : playlist.albums
                    ?.filter((a) => a.albumImageUrl)
                    .map((a, i) => ({
                      '@type': 'ListItem',
                      name: a.title,
                      image: a.albumImageUrl,
                      url: a.slug
                        ? `${SITE_URL}/reviews/${a.slug}`
                        : `${SITE_URL}/reviews/${a.albumSource?.toLowerCase()}/${a.sourceId}`,
                      position: i + 1,
                    }));
            return items?.length > 0 ? (
              <Helmet>
                <script type="application/ld+json">
                  {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    itemListElement: items,
                  })}
                </script>
              </Helmet>
            ) : null;
          })()}
        </>
      )}
      <ProgressBar isLoading={loading}>
        {!playlist ? (
          <NoData />
        ) : (
          <SidebarLayout pageType={PAGE_TYPE.LIST_POST}>
            {/* 헤더 */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: '12px', md: '18px', lg: '20px' },
                pb: { xs: '16px', lg: '24px' },
                borderBottom: `1px solid ${theme.palette.grey.line}`,
              }}
            >
              {/* 헤더 좌측: 썸네일 */}
              <Box
                sx={{
                  width: { xs: '42%', md: '22%', lg: 160 },
                  minWidth: { xs: 132, md: 148 },
                  maxWidth: { xs: 176, md: 160, lg: 160 },
                  flexShrink: 0,
                  alignSelf: { xs: 'center', md: 'flex-start' },
                }}
              >
                <Box
                  component="img"
                  src={playlist.imageUrl || defaultPlaylistThumbnail}
                  alt={playlist.title}
                  onError={(e) => {
                    e.target.src = defaultPlaylistThumbnail;
                  }}
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    display: 'block',
                  }}
                />
              </Box>

              {/* 헤더 우측: 정보 */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  justifyContent: 'center',
                }}
              >
                {/* 제목 + PC 액션 */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: { md: '16px', lg: '24px' },
                  }}
                >
                  <Typography
                    fontSize={{ xs: 14, md: 18 }}
                    fontWeight={'bold'}
                    sx={{ lineHeight: { xs: '1.5', md: '1.15' }, minWidth: 0 }}
                  >
                    {playlist.title}
                  </Typography>
                  {!isXs && renderPlaylistActions()}
                </Box>

                {/* 작성자 */}
                <UserWithProfile
                  username={playlist.username}
                  profileImageUrl={playlist.userProfileImageUrl}
                  userNickname={playlist.username}
                  grade={playlist.userGrade}
                  onClick={() => navigate(`/users/${encodeURIComponent(playlist.username)}`)}
                  sx={{ mt: { xs: '10px', md: '-6px', lg: '-6px' } }}
                  variant="small"
                />

                {/* md/lg: 좌우 분리 (좌: 앨범수/날짜 + 댓글/북마크, 우: 버튼들) */}
                {!isXs && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end',
                      mt: { md: '10px', lg: '12px' },
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box display="flex" gap={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                        <AlbumCountWithDate
                          albumCount={
                            playlist.type === 'TRACK' ? playlist.trackCount : playlist.albumCount
                          }
                          date={playlist.createdAt}
                          type={playlist.type}
                        />
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            height: '8px',
                            alignSelf: 'center',
                            backgroundColor: theme.palette.grey.line,
                          }}
                        />
                        <Box display="flex" gap={1.5} alignItems="center">
                          <CommentCount variant="detail" commentCount={playlist.commentCount} />
                          <BookmarkCount variant="detail" bookmarkCount={playlist.bookmarkCount} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* xs */}
                {isXs && (
                  <>
                    {/* 앨범/트랙 수 */}
                    <AlbumCountWithDate
                      albumCount={
                        playlist.type === 'TRACK' ? playlist.trackCount : playlist.albumCount
                      }
                      type={playlist.type}
                      sx={{ mt: '12px' }}
                    />
                    {/* 날짜 | 댓글 북마크 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '12px' }}>
                      <Typography
                        fontSize={12}
                        fontWeight={'regular'}
                        sx={{ color: theme.palette.black[4] }}
                      >
                        {playlist.createdAt}
                      </Typography>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          height: '8px',
                          alignSelf: 'center',
                          backgroundColor: theme.palette.grey.line,
                        }}
                      />
                      <Box display="flex" gap={1.5} alignItems="center">
                        <CommentCount commentCount={playlist.commentCount} />
                        <BookmarkCount bookmarkCount={playlist.bookmarkCount} />
                      </Box>
                    </Box>
                    {/* 버튼들 */}
                    <Box sx={{ mt: '12px' }}>{renderPlaylistActions()}</Box>
                  </>
                )}
              </Box>
            </Box>

            <Stack spacing={'30px'} sx={{ mt: { xs: '16px', md: '24px', lg: '30px' } }}>
              {/* 본문 */}
              <Box>
                {/* 설명 */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {embedYouTubeInText(playlist.description)?.map((part, i) =>
                    part.type === 'youtube' ? (
                      <Box
                        key={i}
                        sx={{
                          position: 'relative',
                          paddingBottom: '56.25%',
                          height: 0,
                          overflow: 'hidden',
                          maxWidth: '100%',
                          my: 1.5,
                        }}
                      >
                        <iframe
                          src={`https://www.youtube.com/embed/${part.videoId}`}
                          title="YouTube video"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 0,
                          }}
                          allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture"
                          allowFullScreen
                        />
                      </Box>
                    ) : (
                      <Typography
                        key={i}
                        fontSize={14}
                        color={theme.palette.black[4]}
                        component="span"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          lineHeight: 1.5,
                          display: 'block',
                        }}
                      >
                        {part.content}
                      </Typography>
                    ),
                  )}
                </Box>
                {/* 앨범/트랙 리스트 */}
                <Stack
                  spacing={{ xs: 1, lg: 2 }}
                  sx={{ mt: { xs: '16px', md: '24px', lg: '30px' } }}
                >
                  {playlist.type === 'TRACK'
                    ? playlist.tracks?.map((track, index) => (
                        <PlaylistTrackCard
                          key={`${track.id}-${index}`}
                          albumImageUrl={track.albumImageUrl}
                          trackTitle={track.title}
                          artistName={track.artistName}
                          albumTitle={track.albumTitle}
                          releaseDate={track.releaseDate}
                          contentRating={track.contentRating}
                          artistSource={
                            track.artistSource ||
                            (track.artistSourceId ? 'APPLE_MUSIC' : track.albumSource)
                          }
                          artistSourceId={track.artistSourceId}
                          artistSlug={track.artistSlug}
                          artistDbId={track.artistId}
                          albumId={track.albumId}
                          albumSourceId={track.albumSourceId}
                          albumSource={track.albumSource}
                          albumSlug={track.albumSlug}
                          rank={index + 1}
                          referrerSection="playlist"
                        />
                      ))
                    : playlist.albums?.map((album, index) => (
                        <PlaylistAlbumCard
                          key={`${album.id}-${index}`}
                          albumImageUrl={album.albumImageUrl}
                          albumTitle={album.title}
                          artistName={album.artist.name}
                          releaseDate={album.releaseDate}
                          genreNames={album.genreNames || []}
                          contentRating={album.contentRating}
                          albumId={album.id}
                          albumSourceId={album.sourceId}
                          albumSource={album.albumSource}
                          albumSlug={album.slug}
                          artistSource={album.artist.artistSource}
                          artistSourceId={album.artist.sourceId}
                          artistSlug={album.artist.slug}
                          artistDbId={album.artist.id}
                          rank={index + 1}
                          referrerSection="playlist"
                        />
                      ))}
                </Stack>
              </Box>
              {/* 광고 */}
              <InFeedBoxAd pageType={PAGE_TYPE.LIST_POST} />
              {/* 댓글 */}
              <CommentSection
                commentData={comments}
                articleType="play_list"
                articleId={playlist.id}
                contentName={playlist.title}
                onSubmit={handleCommentSubmit}
                onReply={handleCommentReply}
                onDelete={handleCommentDelete}
                commentScrollId={commentScrollId}
              />
            </Stack>
          </SidebarLayout>
        )}
      </ProgressBar>
    </PageLayout>
  );
};

export default PlaylistDetail;
